import { Gender } from './gender';
import { PartnerTitle } from '../../partner/models/partner-title';
import { ShowBasketButtonType } from '../../general/models/show-basket-button-type';
import { TimeSlotDisplayType } from '../../general/models/time-slot-display-type.enum';

export class ShopSettings {
  private _eventTicketsMaximumQuantity: number;
  private _singleTicketsMaximumQuantity: number;
  private _eventTicketsShowForDays: number;
  private _singleTicketsShowForDays: number;
  private _allowTodayBookings: boolean;
  private _startSellTicketsFromDate?: Date;
  private _sessionTimeOut: number;
  private _sessionTimeOutLoggedIn: number;
  private _sessionTimeOutCountdown: number;
  private _orderSessionTimeOut: number;
  private _minimalAgeToSignWaiver: number;
  private _familyParentName: string;
  private _familyChildrenName: string;
  private _familyParentsLimit: number;
  private _familyChildrenLimit: number;
  private _googleAnalyticsKey: string;
  private _gtagGlobalManagerKey: string;
  private _gtagLocalManagerKey: string;
  private _facebookAnalyticsId: string;
  private _genderList: Array<Gender>;
  private _maintenanceWebshopMode: string;
  private _maintenanceWebshopText: string;
  private _partnerTitleList: Array<PartnerTitle>;
  private _logoUrl: string;
  private _themeAssetsUrl: string;
  private _ticketsButtonTitle: string;
  private _eventsButtonTitle: string;
  private _schedulePlanTitle: string;
  private _showFullNameInRegistration: boolean;
  private _showAddressInRegistration: boolean;
  private _showPhoneInRegistration: boolean;
  private _schedulePlanDescription: string;
  private _giftCardsButtonTitle: string;
  private _productsButtonTitle: string;
  private _partnerCodeAuthorization: boolean;
  private _partnerCodePayment: boolean;
  private _isShowProductImages: boolean;
  private _showVUUserMenu: boolean;
  private _showScheduleMenu: boolean;
  private _showContactMenu: boolean;
  private _canResetPassword: boolean;
  private _cardInformationButtonTitle: string;
  private _cardReplaceButtonTitle: string;
  private _multiOrderMode: boolean;
  private _hideDateEndInOrderDetail: boolean;
  private _guestCardProduct: number;
  private _allowGuestCardRefund: boolean;
  private _authorizationRequired: boolean;
  private _logoutByTimeout: boolean;
  private _showTicketInformation: boolean;
  private _vuBaseUrl: string;
  private _contactLink: string;
  private _privacyLink: string;
  private _imprintLink: string;
  private _termsConditionsLink: string;
  private _showBasketButtonType: ShowBasketButtonType;
  private _allowGuestSale: boolean;
  private _timeSlotDisplayType: TimeSlotDisplayType;
  private _groupGiftCardsByCategory: boolean;
  private _hideGiftCardProductName: boolean;
  private _showTermsConditionsLink: boolean;
  private _showPrivacyLink: boolean;
  private _showImprintLink: boolean;
  private _showAvailableTicketsInTimeSlot: boolean;
  private _skipSingleAreaItemPackSelection: boolean;
  private _allowGiftCardPayment: boolean;
  private _showGiftCardsInformationMessage: boolean;
  private _quantityOfAttemptsToCheckPaymentCompletion: number;

  constructor(
    eventTicketsMaximumQuantity: number,
    singleTicketsMaximumQuantity: number,
    eventTicketsShowForDays: number,
    singleTicketsShowForDays: number,
    allowTodayBookings: boolean,
    sessionTimeOut: number,
    sessionTimeOutLoggedIn: number,
    sessionTimeOutCountdown: number,
    orderSessionTimeOut: number,
    minimalAgeToSignWaiver: number,
    familyParentName: string,
    familyChildrenName: string,
    familyParentsLimit: number,
    familyChildrenLimit: number,
    startSellTicketsFromDate?: Date,
    googleAnalyticsKey?: string,
    gtagGlobalManagerKey?: string,
    gtagLocalManagerKey?: string,
    facebookAnalyticsId?: string,
    genderList?: Array<Gender>,
    maintenanceWebshopMode?: string,
    maintenanceWebshopText?: string,
    partnerTitleList?: Array<PartnerTitle>,
    logoUrl?: string,
    themeAssetsUrl?: string,
    ticketsButtonTitle?: string,
    eventsButtonTitle?: string,
    schedulePlanTitle?: string,
    showFullNameInRegistration?: boolean,
    showAddressInRegistration?: boolean,
    showPhoneInRegistration?: boolean,
    schedulePlanDescription?: string,
    giftCardsButtonTitle?: string,
    partnerCodeAuthorization?: boolean,
    partnerCodePayment?: boolean,
    isShowProductImages?: boolean,
    showVUUserMenu?: boolean,
    showScheduleMenu?: boolean,
    showContactMenu?: boolean,
    canResetPassword?: boolean,
    guestCardProduct?: number,
    cardInformationButtonTitle?: string,
    cardReplaceButtonTitle?: string,
    multiOrderMode?: boolean,
    hideDateEndInOrderDetail?: boolean,
    allowGuestCardRefund?: boolean,
    authorizationRequired?: boolean,
    logoutByTimeout?: boolean,
    productsButtonTitle?: string,
    showTicketInformation?: boolean,
    vuBaseUrl?: string,
    contactLink?: string,
    privacyLink?: string,
    imprintLink?: string,
    termsConditionsLink?: string,
    showBasketButtonType?: ShowBasketButtonType,
    allowGuestSale?: boolean,
    timeSlotDisplayType?: TimeSlotDisplayType,
    groupGiftCardsByCategory?: boolean,
    hideGiftCardProductName?: boolean,
    showTermsConditionsLink?: boolean,
    showPrivacyLink?: boolean,
    showImprintLink?: boolean,
    showAvailableTicketsInTimeSlot?: boolean,
    skipSingleAreaItemPackSelection?: boolean,
    allowGiftCardPayment?: boolean,
    showGiftCardsInformationMessage?: boolean,
    quantityOfAttemptsToCheckPaymentCompletion?: number,
  ) {
    this._eventTicketsMaximumQuantity = eventTicketsMaximumQuantity;
    this._singleTicketsMaximumQuantity = singleTicketsMaximumQuantity;
    this._eventTicketsShowForDays = eventTicketsShowForDays;
    this._singleTicketsShowForDays = singleTicketsShowForDays;
    this._allowTodayBookings = allowTodayBookings;
    this._sessionTimeOut = sessionTimeOut;
    this._sessionTimeOutLoggedIn = sessionTimeOutLoggedIn;
    this._sessionTimeOutCountdown = sessionTimeOutCountdown;
    this._orderSessionTimeOut = orderSessionTimeOut;
    this._minimalAgeToSignWaiver = minimalAgeToSignWaiver;
    this._familyParentName = familyParentName;
    this._familyChildrenName = familyChildrenName;
    this._familyParentsLimit = familyParentsLimit;
    this._familyChildrenLimit = familyChildrenLimit;
    this._startSellTicketsFromDate = startSellTicketsFromDate;
    this._googleAnalyticsKey = googleAnalyticsKey;
    this._gtagLocalManagerKey = gtagLocalManagerKey;
    this._gtagGlobalManagerKey = gtagGlobalManagerKey;
    this._facebookAnalyticsId = facebookAnalyticsId;
    this._genderList = genderList;
    this._maintenanceWebshopMode = maintenanceWebshopMode;
    this._maintenanceWebshopText = maintenanceWebshopText;
    this._partnerTitleList = partnerTitleList;
    this._logoUrl = logoUrl;
    this._themeAssetsUrl = themeAssetsUrl;
    this._ticketsButtonTitle = ticketsButtonTitle;
    this._eventsButtonTitle = eventsButtonTitle;
    this._schedulePlanTitle = schedulePlanTitle;
    this._showFullNameInRegistration = showFullNameInRegistration;
    this._showAddressInRegistration = showAddressInRegistration;
    this._showPhoneInRegistration = showPhoneInRegistration;
    this._schedulePlanDescription = schedulePlanDescription;
    this._giftCardsButtonTitle = giftCardsButtonTitle;
    this._partnerCodeAuthorization = partnerCodeAuthorization;
    this._partnerCodePayment = partnerCodePayment;
    this._isShowProductImages = isShowProductImages;
    this._showVUUserMenu = showVUUserMenu;
    this._showScheduleMenu = showScheduleMenu;
    this._showContactMenu = showContactMenu;
    this._canResetPassword = canResetPassword;
    this._guestCardProduct = guestCardProduct;
    this._cardInformationButtonTitle = cardInformationButtonTitle;
    this._cardReplaceButtonTitle = cardReplaceButtonTitle;
    this._multiOrderMode = multiOrderMode;
    this._hideDateEndInOrderDetail = hideDateEndInOrderDetail;
    this._allowGuestCardRefund = allowGuestCardRefund;
    this._authorizationRequired = authorizationRequired;
    this._logoutByTimeout = logoutByTimeout;
    this._productsButtonTitle = productsButtonTitle;
    this._showTicketInformation = showTicketInformation;
    this._vuBaseUrl = vuBaseUrl;
    this._contactLink = contactLink;
    this._privacyLink = privacyLink;
    this._imprintLink = imprintLink;
    this._termsConditionsLink = termsConditionsLink;
    this._showBasketButtonType = showBasketButtonType || ShowBasketButtonType.None;
    this._allowGuestSale = allowGuestSale;
    this._timeSlotDisplayType = timeSlotDisplayType;
    this._groupGiftCardsByCategory = groupGiftCardsByCategory;
    this._hideGiftCardProductName = hideGiftCardProductName;
    this._showTermsConditionsLink = showTermsConditionsLink;
    this._showPrivacyLink = showPrivacyLink;
    this._showImprintLink = showImprintLink;
    this._showAvailableTicketsInTimeSlot = showAvailableTicketsInTimeSlot;
    this._skipSingleAreaItemPackSelection = skipSingleAreaItemPackSelection;
    this._allowGiftCardPayment = allowGiftCardPayment;
    this._showGiftCardsInformationMessage = showGiftCardsInformationMessage;
    this._quantityOfAttemptsToCheckPaymentCompletion = quantityOfAttemptsToCheckPaymentCompletion;
  }

  static createFromAny(object: any): ShopSettings {
    if (object === null || object === undefined) {
      return null;
    }

    return new ShopSettings(
      object.eventTicketsMaximumQuantity,
      object.singleTicketsMaximumQuantity,
      object.eventTicketsShowForDays,
      object.singleTicketsShowForDays,
      object.allowTodayBookings,
      object.sessionTimeOut,
      object.sessionTimeOutLoggedIn,
      object.sessionTimeOutCountdown,
      object.orderSessionTimeOut,
      object.minimalAgeToSignWaiver,
      object.familyParentName,
      object.familyChildrenName,
      object.familyParentsLimit,
      object.familyChildrenLimit,
      object.startSellTicketsFromDate ? new Date(object.startSellTicketsFromDate) : null,
      object.googleAnalyticsKey,
      object.gtagGlobalManagerKey,
      object.gtagLocalManagerKey,
      object.facebookAnalyticsId,
      object.genderList ? object.genderList.map(x => Gender.createFromAny(x)) : [],
      object.maintenanceWebshopMode,
      object.maintenanceWebshopText ? object.maintenanceWebshopText : '',
      object.partnerTitleList ? object.partnerTitleList.map(x => PartnerTitle.createFromAny(x)) : [],
      object.customLogoUrl,
      object.themeAssetsUrl,
      object.ticketsButtonTitle,
      object.eventsButtonTitle,
      object.schedulePlanTitle,
      object.showFullNameInRegistration,
      object.showAddressInRegistration,
      object.showPhoneInRegistration,
      object.schedulePlanDescription,
      object.giftCardsButtonTitle,
      object.partnerCodeAuthorization,
      object.partnerCodePayment,
      object.isShowProductImages,
      object.showVUUserMenu,
      object.showScheduleMenu,
      object.showContactMenu,
      object.canResetPassword,
      object.guestCardProduct,
      object.cardInformationButtonTitle,
      object.cardReplaceButtonTitle,
      object.multiOrderMode,
      object.hideDateEndInOrderDetail,
      object.allowGuestCardRefund,
      object.authorizationRequired,
      object.logoutByTimeout,
      object.productsButtonTitle,
      object.showTicketInformation,
      object.vuBaseUrl,
      object.contactLink,
      object.privacyLink,
      object.imprintLink,
      object.termsConditionsLink,
      object.showBasketButtonType,
      object.allowGuestSale,
      TimeSlotDisplayType[object.timeSlotDisplayType] || TimeSlotDisplayType.Default,
      object.groupGiftCardsByCategory,
      object.hideGiftCardProductName,
      object.showTermsConditionsLink,
      object.showPrivacyLink,
      object.showImprintLink,
      object.showAvailableTicketsInTimeSlot,
      object.skipSingleAreaItemPackSelection,
      object.allowGiftCardPayment,
      object.showGiftCardsInformationMessage,
      object.quantityOfAttemptsToCheckPaymentCompletion,
    );
  }

  get eventTicketsMaximumQuantity(): number {
    return this._eventTicketsMaximumQuantity;
  }

  get singleTicketsMaximumQuantity(): number {
    return this._singleTicketsMaximumQuantity;
  }

  get eventTicketsShowForDays(): number {
    return this._eventTicketsShowForDays;
  }

  get singleTicketsShowForDays(): number {
    return this._singleTicketsShowForDays;
  }

  get allowTodayBookings(): boolean {
    return this._allowTodayBookings;
  }

  get startSellTicketsFromDate(): Date {
    return this._startSellTicketsFromDate;
  }

  get sessionTimeOut(): number {
    return this._sessionTimeOut;
  }

  get sessionTimeOutLoggedIn(): number {
    return this._sessionTimeOutLoggedIn;
  }

  get sessionTimeOutCountdown(): number {
    return this._sessionTimeOutCountdown;
  }

  get orderSessionTimeOut(): number {
    return this._orderSessionTimeOut;
  }

  get googleAnalyticsKey(): string {
    return this._googleAnalyticsKey;
  }

  get gtagGlobalManagerKey(): string {
    return this._gtagGlobalManagerKey;
  }

  get gtagLocalManagerKey(): string {
    return this._gtagLocalManagerKey;
  }

  get facebookAnalyticsId(): string {
    return this._facebookAnalyticsId;
  }

  get minimalAgeToSignWaiver(): number {
    return this._minimalAgeToSignWaiver;
  }

  get familyParentName(): string {
    return this._familyParentName;
  }

  get familyChildrenName(): string {
    return this._familyChildrenName;
  }

  get familyParentsLimit(): number {
    return this._familyParentsLimit;
  }

  get familyChildrenLimit(): number {
    return this._familyChildrenLimit;
  }

  get genderList(): Array<Gender> {
    return this._genderList;
  }

  get maintenanceWebshopMode(): string {
    return this._maintenanceWebshopMode;
  }

  get maintenanceWebshopText(): string {
    return this._maintenanceWebshopText;
  }

  get partnerTitleList(): Array<PartnerTitle> {
    return this._partnerTitleList;
  }

  get logoUrl(): string {
    return this._logoUrl;
  }

  get themeAssetsUrl(): string {
    return this._themeAssetsUrl;
  }

  get ticketsButtonTitle(): string {
    return this._ticketsButtonTitle;
  }

  get eventsButtonTitle(): string {
    return this._eventsButtonTitle;
  }

  get schedulePlanTitle(): string {
    return this._schedulePlanTitle;
  }

  get showFullNameInRegistration(): boolean {
    return this._showFullNameInRegistration;
  }

  get showAddressInRegistration(): boolean {
    return this._showAddressInRegistration;
  }

  get showPhoneInRegistration(): boolean {
    return this._showPhoneInRegistration;
  }

  get schedulePlanDescription(): string {
    return this._schedulePlanDescription;
  }

  get giftCardsButtonTitle(): string {
    return this._giftCardsButtonTitle;
  }

  get productsButtonTitle(): string {
    return this._productsButtonTitle;
  }

  get partnerCodeAuthorization(): boolean {
    return this._partnerCodeAuthorization;
  }

  get partnerCodePayment(): boolean {
    return this._partnerCodePayment;
  }

  get isShowProductImages(): boolean {
    return this._isShowProductImages;
  }

  get showVUUserMenu(): boolean {
    return this._showVUUserMenu;
  }

  get showScheduleMenu(): boolean {
    return this._showScheduleMenu;
  }

  get showContactMenu(): boolean {
    return this._showContactMenu;
  }

  get showTicketInformation(): boolean {
    return this._showTicketInformation;
  }

  get canResetPassword(): boolean {
    return this._canResetPassword;
  }

  get guestCardProduct(): number {
    return this._guestCardProduct;
  }

  get cardInformationButtonTitle(): string {
    return this._cardInformationButtonTitle;
  }

  get cardReplaceButtonTitle(): string {
    return this._cardReplaceButtonTitle;
  }

  get multiOrderMode(): boolean {
    return this._multiOrderMode;
  }

  get hideDateEndInOrderDetail(): boolean {
    return this._hideDateEndInOrderDetail;
  }

  get allowGuestCardRefund(): boolean {
    return this._allowGuestCardRefund;
  }

  get authorizationRequired(): boolean {
    return this._authorizationRequired;
  }

  get logoutByTimeout(): boolean {
    return this._logoutByTimeout;
  }

  get vuBaseUrl(): string {
    return this._vuBaseUrl;
  }

  get contactLink(): string {
    return this._contactLink;
  }

  get privacyLink(): string {
    return this._privacyLink;
  }

  get imprintLink(): string {
    return this._imprintLink;
  }

  get termsConditionsLink(): string {
    return this._termsConditionsLink;
  }


  get showBasketButtonType(): ShowBasketButtonType {
    return this._showBasketButtonType;
  }

  get allowGuestSale(): boolean {
    return this._allowGuestSale;
  }
  get timeSlotDisplayType(): TimeSlotDisplayType {
    return this._timeSlotDisplayType;
  }

  get groupGiftCardsByCategory(): boolean {
    return this._groupGiftCardsByCategory;
  }

  get hideGiftCardProductName(): boolean {
    return this._hideGiftCardProductName;
  }

  get showTermsConditionsLink(): boolean {
    return this._showTermsConditionsLink;
  }

  get showPrivacyLink(): boolean {
    return this._showPrivacyLink;
  }

  get showImprintLink(): boolean {
    return this._showImprintLink;
  }

  get showAvailableTicketsInTimeSlot(): boolean {
    return this._showAvailableTicketsInTimeSlot;
  }

  get skipSingleAreaItemPackSelection(): boolean {
    return this._skipSingleAreaItemPackSelection;
  }

  get allowGiftCardPayment(): boolean {
    return this._allowGiftCardPayment;
  }

  get showGiftCardsInformationMessage(): boolean {
    return this._showGiftCardsInformationMessage;
  }

  get quantityOfAttemptsToCheckPaymentCompletion(): number {
    return this._quantityOfAttemptsToCheckPaymentCompletion;
  }
}
